import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  MapPin, 
  Star, 
  Video, 
  Users, 
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Euro,
  Calendar,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageTracking } from '../hooks/useAnalytics';
import { trackEvent, TRACKABLE_EVENTS } from '../services/analyticsService';
import VoiceInterfaceCombined from '../components/VoiceInterfaceCombined';
import { useActiveAssistant } from '../hooks/useActiveAssistant';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Healthcare specialties - Complete list
const SPECIALTIES = [
  { id: 'all', name: 'Toutes spécialités' },
  
  // === MÉDECINS GÉNÉRALISTES ===
  { id: 'general', name: 'Médecin Généraliste' },
  
  // === SPÉCIALISTES MÉDICAUX ===
  { id: 'internist', name: 'Interniste / Médecine Interne' },
  { id: 'cardiologist', name: 'Cardiologue' },
  { id: 'dermatologist', name: 'Dermatologue' },
  { id: 'gastroenterologist', name: 'Gastro-entérologue' },
  { id: 'pulmonologist', name: 'Pneumologue' },
  { id: 'neurologist', name: 'Neurologue' },
  { id: 'psychiatrist', name: 'Psychiatre' },
  { id: 'psychologist', name: 'Psychologue' },
  { id: 'endocrinologist', name: 'Endocrinologue' },
  { id: 'rheumatologist', name: 'Rhumatologue' },
  { id: 'nephrologist', name: 'Néphrologue' },
  { id: 'urologist', name: 'Urologue' },
  { id: 'gynecologist', name: 'Gynécologue' },
  { id: 'obstetrician', name: 'Obstétricien' },
  { id: 'pediatrician', name: 'Pédiatre' },
  { id: 'geriatrician', name: 'Gériatre' },
  { id: 'oncologist', name: 'Oncologue' },
  { id: 'hematologist', name: 'Hématologue' },
  { id: 'allergist', name: 'Allergologue' },
  { id: 'immunologist', name: 'Immunologue' },
  { id: 'infectiologist', name: 'Infectiologue' },
  
  // === CHIRURGIENS ===
  { id: 'general_surgeon', name: 'Chirurgien Général' },
  { id: 'orthopedist', name: 'Chirurgien Orthopédiste' },
  { id: 'cardiac_surgeon', name: 'Chirurgien Cardiaque' },
  { id: 'neurosurgeon', name: 'Neurochirurgien' },
  { id: 'plastic_surgeon', name: 'Chirurgien Plasticien / Esthétique' },
  { id: 'vascular_surgeon', name: 'Chirurgien Vasculaire' },
  { id: 'thoracic_surgeon', name: 'Chirurgien Thoracique' },
  { id: 'digestive_surgeon', name: 'Chirurgien Digestif' },
  { id: 'urological_surgeon', name: 'Chirurgien Urologue' },
  
  // === ANESTHÉSIE & RÉANIMATION ===
  { id: 'anesthesiologist', name: 'Anesthésiste-Réanimateur' },
  
  // === SPÉCIALISTES ORL, YEUX, DENTS ===
  { id: 'ophthalmologist', name: 'Ophtalmologue' },
  { id: 'ent_specialist', name: 'ORL (Oto-Rhino-Laryngologue)' },
  { id: 'dentist', name: 'Dentiste' },
  { id: 'orthodontist', name: 'Orthodontiste' },
  { id: 'oral_surgeon', name: 'Chirurgien-Dentiste / Stomatologiste' },
  
  // === RADIOLOGIE & IMAGERIE ===
  { id: 'radiologist', name: 'Radiologue' },
  { id: 'nuclear_medicine', name: 'Médecine Nucléaire' },
  
  // === MÉDECINE PHYSIQUE ===
  { id: 'physiatrist', name: 'Médecin Rééducateur (MPR)' },
  { id: 'sports_medicine', name: 'Médecin du Sport' },
  
  // === AUTRES SPÉCIALITÉS MÉDICALES ===
  { id: 'emergency_medicine', name: 'Médecin Urgentiste' },
  { id: 'occupational_medicine', name: 'Médecin du Travail' },
  { id: 'pathologist', name: 'Anatomo-Pathologiste' },
  { id: 'medical_geneticist', name: 'Généticien Médical' },
  
  // === PARAMÉDICAL - RÉÉDUCATION ===
  { id: 'physiotherapist', name: 'Kinésithérapeute' },
  { id: 'osteopath', name: 'Ostéopathe' },
  { id: 'chiropractor', name: 'Chiropracteur' },
  { id: 'speech_therapist', name: 'Orthophoniste' },
  { id: 'occupational_therapist', name: 'Ergothérapeute' },
  { id: 'psychomotor_therapist', name: 'Psychomotricien' },
  
  // === PARAMÉDICAL - SOINS ===
  { id: 'nurse', name: 'Infirmier(ère)' },
  { id: 'midwife', name: 'Sage-Femme' },
  { id: 'dietitian', name: 'Diététicien(ne) / Nutritionniste' },
  
  // === PARAMÉDICAL - PODOLOGIE & PÉDICURE ===
  { id: 'podiatrist', name: 'Podologue' },
  { id: 'pedicurist', name: 'Pédicure' },
  { id: 'pedicure_podiatrist', name: 'Pédicure-Podologue' },
  
  // === BIEN-ÊTRE & MÉDECINES DOUCES ===
  { id: 'reflexologist', name: 'Réflexologue' },
  { id: 'acupuncturist', name: 'Acupuncteur' },
  { id: 'naturopath', name: 'Naturopathe' },
  { id: 'homeopath', name: 'Homéopathe' },
  { id: 'sophrologist', name: 'Sophrologue' },
  { id: 'hypnotherapist', name: 'Hypnothérapeute' },
  
  // === ESTHÉTIQUE & BEAUTÉ ===
  { id: 'aesthetician', name: 'Esthéticien(ne)' },
  { id: 'aesthetic_doctor', name: 'Médecin Esthétique' },
  
  // === OPTIQUE & AUDITION ===
  { id: 'optician', name: 'Opticien' },
  { id: 'audiologist', name: 'Audioprothésiste' },
  
  // === PHARMACIE ===
  { id: 'pharmacist', name: 'Pharmacien' },
  
  // === LABORATOIRE ===
  { id: 'biologist', name: 'Biologiste Médical' },
];

const SORT_OPTIONS = [
  { value: 'distance', label: 'Distance' },
  { value: 'rating', label: 'Meilleure note' },
  { value: 'price', label: 'Prix le plus bas' },
];

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeAssistantId = useActiveAssistant();
  
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Search filters
  const [filters, setFilters] = useState({
    name: searchParams.get('name') || '',
    city: searchParams.get('city') || '',
    specialty: searchParams.get('specialty') || 'all',
    minRating: 0,
    maxPrice: 200,
    acceptsNewPatients: false,
    teleconsultationEnabled: false,
    availableToday: false,
    sortBy: 'distance',
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      name: '',
      city: '',
      specialty: 'all',
      minRating: 0,
      maxPrice: 200,
      acceptsNewPatients: false,
      teleconsultationEnabled: false,
      availableToday: false,
      sortBy: 'distance',
    });
  };

  const performSearch = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const searchPayload = {
        name: filters.name || undefined,
        city: filters.city || undefined,
        specialty: filters.specialty !== 'all' ? filters.specialty : undefined,
        min_rating: filters.minRating > 0 ? filters.minRating : undefined,
        max_price: filters.maxPrice < 200 ? filters.maxPrice : undefined,
        accepts_new_patients: filters.acceptsNewPatients || undefined,
        teleconsultation_enabled: filters.teleconsultationEnabled || undefined,
        available_today: filters.availableToday || undefined,
        sort_by: filters.sortBy,
        page: page,
        limit: 10,
      };

      // Remove undefined values
      Object.keys(searchPayload).forEach(key => {
        if (searchPayload[key] === undefined) {
          delete searchPayload[key];
        }
      });

      const response = await fetch(`${API_URL}/api/professionals/search/advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchPayload),
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults(data.results || []);
      setTotalResults(data.total || 0);
      setCurrentPage(page);
    } catch (error) {
      toast.error('Erreur lors de la recherche');
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(1);
  };

  const activeFiltersCount = [
    filters.minRating > 0,
    filters.maxPrice < 200,
    filters.acceptsNewPatients,
    filters.teleconsultationEnabled,
    filters.availableToday,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Recherche Avancée
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Trouvez le professionnel de santé idéal avec nos filtres avancés
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filtres
                    {activeFiltersCount > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {activeFiltersCount}
                      </Badge>
                    )}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden"
                  >
                    {showFilters ? <ChevronUp /> : <ChevronDown />}
                  </Button>
                </div>
              </CardHeader>
              
              {showFilters && (
                <CardContent className="space-y-6">
                  {/* Name Search */}
                  <div className="space-y-2">
                    <Label>Nom du praticien</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Dr. Martin..."
                        value={filters.name}
                        onChange={(e) => handleFilterChange('name', e.target.value)}
                        className="pl-10"
                        data-testid="filter-name"
                      />
                    </div>
                  </div>

                  {/* City */}
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Paris, Lyon..."
                        value={filters.city}
                        onChange={(e) => handleFilterChange('city', e.target.value)}
                        className="pl-10"
                        data-testid="filter-city"
                      />
                    </div>
                  </div>

                  {/* Specialty */}
                  <div className="space-y-2">
                    <Label>Spécialité</Label>
                    <Select
                      value={filters.specialty}
                      onValueChange={(value) => handleFilterChange('specialty', value)}
                    >
                      <SelectTrigger data-testid="filter-specialty">
                        <SelectValue placeholder="Choisir une spécialité" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIALTIES.map((spec) => (
                          <SelectItem key={spec.id} value={spec.id}>
                            {spec.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Min Rating */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      Note minimale: {filters.minRating > 0 ? filters.minRating : 'Toutes'}
                    </Label>
                    <Slider
                      value={[filters.minRating]}
                      onValueChange={([value]) => handleFilterChange('minRating', value)}
                      max={5}
                      step={0.5}
                      className="py-2"
                      data-testid="filter-rating"
                    />
                  </div>

                  {/* Max Price */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-emerald-500" />
                      Prix max: {filters.maxPrice < 200 ? `${filters.maxPrice}€` : 'Illimité'}
                    </Label>
                    <Slider
                      value={[filters.maxPrice]}
                      onValueChange={([value]) => handleFilterChange('maxPrice', value)}
                      max={200}
                      step={10}
                      className="py-2"
                      data-testid="filter-price"
                    />
                  </div>

                  {/* Toggle Filters */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <Users className="w-4 h-4 text-blue-500" />
                        Accepte nouveaux patients
                      </Label>
                      <Switch
                        checked={filters.acceptsNewPatients}
                        onCheckedChange={(checked) => handleFilterChange('acceptsNewPatients', checked)}
                        data-testid="filter-new-patients"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <Video className="w-4 h-4 text-purple-500" />
                        Téléconsultation
                      </Label>
                      <Switch
                        checked={filters.teleconsultationEnabled}
                        onCheckedChange={(checked) => handleFilterChange('teleconsultationEnabled', checked)}
                        data-testid="filter-teleconsultation"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <Calendar className="w-4 h-4 text-emerald-500" />
                        Disponible aujourd'hui
                      </Label>
                      <Switch
                        checked={filters.availableToday}
                        onCheckedChange={(checked) => handleFilterChange('availableToday', checked)}
                        data-testid="filter-available-today"
                      />
                    </div>
                  </div>

                  {/* Sort */}
                  <div className="space-y-2 pt-4 border-t">
                    <Label>Trier par</Label>
                    <Select
                      value={filters.sortBy}
                      onValueChange={(value) => handleFilterChange('sortBy', value)}
                    >
                      <SelectTrigger data-testid="filter-sort">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={handleSearch}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                      data-testid="btn-search"
                    >
                      {loading ? 'Recherche...' : 'Rechercher'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetFilters}
                      data-testid="btn-reset"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-slate-600 dark:text-slate-400">
                {totalResults > 0 ? (
                  <>
                    <span className="font-semibold text-slate-900 dark:text-white">{totalResults}</span> résultat{totalResults > 1 ? 's' : ''} trouvé{totalResults > 1 ? 's' : ''}
                  </>
                ) : (
                  'Utilisez les filtres pour rechercher des professionnels'
                )}
              </p>
            </div>

            {/* Results Grid */}
            {loading ? (
              <div className="grid gap-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <div className="w-20 h-20 bg-slate-200 rounded-full" />
                        <div className="flex-1 space-y-3">
                          <div className="h-5 bg-slate-200 rounded w-1/3" />
                          <div className="h-4 bg-slate-200 rounded w-1/2" />
                          <div className="h-4 bg-slate-200 rounded w-1/4" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : results.length > 0 ? (
              <div className="grid gap-4">
                {results.map((professional) => (
                  <Card 
                    key={professional.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/professional/${professional.id}`)}
                    data-testid={`result-${professional.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        {/* Avatar */}
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                          {professional.first_name?.[0]}{professional.last_name?.[0]}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                                Dr. {professional.first_name} {professional.last_name}
                              </h3>
                              <p className="text-blue-600 dark:text-blue-400 font-medium">
                                {professional.specialty || 'Médecin'}
                              </p>
                            </div>
                            
                            {/* Rating */}
                            <div className="flex items-center gap-1 bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 rounded-lg">
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              <span className="font-medium text-yellow-700 dark:text-yellow-400">
                                {professional.rating?.toFixed(1) || 'N/A'}
                              </span>
                              <span className="text-xs text-slate-500">
                                ({professional.review_count || 0})
                              </span>
                            </div>
                          </div>
                          
                          {/* Details */}
                          <div className="mt-2 flex flex-wrap gap-2 text-sm text-slate-600 dark:text-slate-400">
                            {professional.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {professional.city}
                              </span>
                            )}
                            {professional.distance_km && (
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {professional.distance_km} km
                              </span>
                            )}
                            {professional.consultation_fee && (
                              <span className="flex items-center gap-1">
                                <Euro className="w-3 h-3" />
                                {professional.consultation_fee}€
                              </span>
                            )}
                          </div>
                          
                          {/* Badges */}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {professional.teleconsultation_enabled && (
                              <Badge variant="outline" className="text-purple-600 border-purple-300">
                                <Video className="w-3 h-3 mr-1" />
                                Téléconsultation
                              </Badge>
                            )}
                            {professional.accepts_new_patients && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                                <Users className="w-3 h-3 mr-1" />
                                Accepte nouveaux patients
                              </Badge>
                            )}
                            {professional.is_verified && (
                              <Badge className="bg-blue-100 text-blue-700">
                                Vérifié
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Pagination */}
                {totalResults > 10 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      disabled={currentPage === 1}
                      onClick={() => performSearch(currentPage - 1)}
                    >
                      Précédent
                    </Button>
                    <span className="flex items-center px-4 text-sm text-slate-600">
                      Page {currentPage} sur {Math.ceil(totalResults / 10)}
                    </span>
                    <Button
                      variant="outline"
                      disabled={currentPage >= Math.ceil(totalResults / 10)}
                      onClick={() => performSearch(currentPage + 1)}
                    >
                      Suivant
                    </Button>
                  </div>
                )}
              </div>
            ) : totalResults === 0 && results.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Search className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Aucun résultat
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    Essayez de modifier vos critères de recherche
                  </p>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>
      </div>
      
      {/* Voice Assistant Floating Button with Fallback */}
      <VoiceInterfaceCombined 
        assistantId={activeAssistantId}
        onError={(e) => toast.error('Erreur assistant vocal: ' + e.message)}
      />
    </div>
  );
};

export default AdvancedSearch;
